import { Component, inject, signal, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { OrdersTab } from '../merchants/orders-tab';

// Top-level Orders page. Reads the event-sourced Orders module (per-location order book) via the
// shared OrdersTab component. The order book is location-scoped and read-only (no Refetch/Replay):
// pick a merchant first (or deep-link with ?locationId=X) and OrdersTab owns list + detail rendering.
@Component({
    selector: 'app-orders',
    standalone: true,
    imports: [CardModule, SelectModule, MessageModule, FormsModule, OrdersTab],
    template: `
        <p-card header="Orders">

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="merchantOptions()" [(ngModel)]="locationId" (onChange)="onSelect()"
                          placeholder="Select a merchant" [filter]="true" styleClass="w-72" />
            </div>

            @if (activeLocation(); as loc) {
                <app-orders-tab [locationId]="loc" [initialSearch]="search" />
            } @else if (loadError()) {
                <p-message severity="error">{{ loadError() }}</p-message>
            } @else {
                <p class="text-surface-500 dark:text-surface-400">Select a merchant to view its orders.</p>
            }
        </p-card>
    `
})
export class Orders implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly route = inject(ActivatedRoute);

    // The chosen merchant (a location). null until picked — nothing loads before that.
    locationId: string | null = null;
    // Deep-link: /orders?locationId=X&search=Y opens that merchant's list already searched (the Order hand-off page
    // links a failing hand-off to its order this way).
    search: string | null = null;
    // The location actually mounted into OrdersTab. Kept separate so a merchant switch can remount
    // the tab (its [lazy] table re-fires onLazyLoad on render — the input change alone would not reload).
    readonly activeLocation = signal<string | null>(null);
    readonly merchantOptions = signal<{ label: string; value: string }[]>([]);
    readonly loadError = signal<string | null>(null);

    ngOnInit(): void {
        // Deep-link support: /orders?locationId=X pre-selects that merchant.
        this.locationId = this.route.snapshot.queryParamMap.get('locationId');
        this.search = this.route.snapshot.queryParamMap.get('search');
        this.activeLocation.set(this.locationId);
        // Merchants = onboarded locations (never Magento instances).
        this.api.getLocations().subscribe({
            next: (r) => this.merchantOptions.set(
                (r.locations ?? []).map((l) => ({ label: l.name ?? l.locationId, value: l.locationId }))
            ),
            error: (e) => this.loadError.set(this.describe(e))
        });
    }

    onSelect(): void {
        // Remount OrdersTab so its lazy table re-fires onLazyLoad and loads the new merchant.
        this.activeLocation.set(null);
        setTimeout(() => this.activeLocation.set(this.locationId));
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — your role lacks the orders:read permission.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
